import { PresetTableCell } from './PresetTableCell';
import type { SetTableViewProps } from './useSeededKidSetTable';
import {
  presetTableBase,
  presetTableCellBase,
  presetTableHeaderCellBase,
} from './useTableStyles';

type CompositionRow = {
  formRowIndex: number;
  category: string;
  categoryRowSpan?: number;
  costType: string;
  value: string;
  description: string;
};

function buildCompositionRows(dataRows: string[][]): CompositionRow[] {
  const rows: CompositionRow[] = [];

  dataRows.forEach((row, rowIndex) => {
    const formRowIndex = rowIndex + 1;
    const category = row[0] ?? '';
    if (category) {
      let rowSpan = 1;
      for (let i = rowIndex + 1; i < dataRows.length; i += 1) {
        if (dataRows[i][0]) break;
        rowSpan += 1;
      }
      rows.push({
        formRowIndex,
        category,
        categoryRowSpan: rowSpan,
        costType: row[1] ?? '',
        value: row[2] ?? '',
        description: row[3] ?? '',
      });
    } else {
      rows.push({
        formRowIndex,
        category: '',
        costType: row[1] ?? '',
        value: row[2] ?? '',
        description: row[3] ?? '',
      });
    }
  });

  return rows;
}

/**
 * Composition of Costs preset table (presentational).
 * Marker-injected cells (COST entry/exit/transaction/ongoing/performance) are read-only.
 */
export function CompositionOfCostsTable({
  content,
  updateCell,
  isInjectedCell,
}: SetTableViewProps) {
  const [bannerRow, ...dataRows] = content;
  const colCount = bannerRow.length;
  const rows = buildCompositionRows(dataRows);

  return (
    <table className={presetTableBase}>
      <colgroup>
        <col style={{ width: '12%' }} />
        <col style={{ width: '24%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '52%' }} />
      </colgroup>
      <thead>
        <tr>
          <th
            colSpan={colCount}
            className={`${presetTableHeaderCellBase} text-center`}
          >
            <PresetTableCell
              value={bannerRow[0] ?? ''}
              rowIndex={0}
              colIndex={0}
              isInjected={isInjectedCell(0, 0)}
              onUpdate={updateCell}
            />
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isCategoryStart = Boolean(row.category);

          return (
            <tr key={row.formRowIndex}>
              {isCategoryStart ? (
                <td
                  rowSpan={row.categoryRowSpan}
                  className={`${presetTableCellBase} text-center font-semibold`}
                >
                  <PresetTableCell
                    value={row.category}
                    rowIndex={row.formRowIndex}
                    colIndex={0}
                    isInjected={isInjectedCell(row.formRowIndex, 0)}
                    onUpdate={updateCell}
                    multiline
                  />
                </td>
              ) : null}
              <td className={presetTableCellBase}>
                <PresetTableCell
                  value={row.costType}
                  rowIndex={row.formRowIndex}
                  colIndex={1}
                  isInjected={isInjectedCell(row.formRowIndex, 1)}
                  onUpdate={updateCell}
                  multiline
                />
              </td>
              <td className={`${presetTableCellBase} text-center`}>
                <PresetTableCell
                  value={row.value}
                  rowIndex={row.formRowIndex}
                  colIndex={2}
                  isInjected={isInjectedCell(row.formRowIndex, 2)}
                  onUpdate={updateCell}
                />
              </td>
              <td className={presetTableCellBase}>
                <PresetTableCell
                  value={row.description}
                  rowIndex={row.formRowIndex}
                  colIndex={3}
                  isInjected={isInjectedCell(row.formRowIndex, 3)}
                  onUpdate={updateCell}
                  multiline
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
